/**
 * SIGGO — garantiza que la tarjeta de producto muestre SIEMPRE su imagen
 * principal al cargar, sin depender de ningun evento previo del puntero.
 *
 * POR QUE HACE FALTA
 * En Horizon, la imagen visible de una tarjeta no la decide una clase ni una
 * opacidad: la decide la POSICION DE SCROLL HORIZONTAL de `slideshow-slides`,
 * que es un scroller con `scroll-snap-type: x mandatory` y una diapositiva por
 * imagen. El efecto de hover (`previewImage` en `assets/product-card.js`) se
 * limita a llamar a `slideshow.next()`, que desplaza ese scroller.
 *
 * El problema es que en la ruta normal de arranque NADIE fija esa posicion.
 * En `assets/slideshow.js`, `#setupSlideshow()` solo llama a `select(0)` si
 * `#updateVisibleSlides()` devuelve cero diapositivas visibles; si devuelve una,
 * da por hecho que el scroller ya esta en el origen. Es una suposicion, no una
 * garantia, y se rompe cuando algo mueve el scroller antes de tiempo:
 *
 *   - el navegador RESTAURA la posicion de los scrollers internos al volver
 *     atras o al restaurar desde bfcache;
 *   - la segunda diapositiva llega con `content-visibility: auto` y tamaño cero
 *     y, al resolverse, el ancla obligatoria de `scroll-snap` se reevalua y
 *     puede engancharse a la diapositiva equivocada;
 *   - con anchos fraccionarios (pantallas a DPR 2) el punto de anclaje no cae
 *     exacto en cero y el scroller queda a medio camino: las DOS imagenes
 *     visibles a la vez, que es justo lo que se reporto.
 *
 * Como no hay nada que recalcule esa posicion despues, el estado erroneo
 * persiste hasta que el usuario pasa el raton por encima: el hover "arregla" la
 * tarjeta porque es la primera vez que alguien fija el scroll a proposito.
 *
 * QUE HACE ESTE ARCHIVO
 * Convierte esa suposicion en una garantia: fija el scroller al origen en los
 * momentos en que puede haber quedado descolocado (arranque, restauracion de
 * bfcache, cambios de tamaño y llegada tardia de imagenes) y deja de hacerlo en
 * cuanto el usuario interactua con esa tarjeta, para no pelearse ni con el
 * hover ni con el selector de variante.
 *
 * No modifica ningun archivo del tema.
 */
(() => {
  const SELECTOR = '.card-gallery';
  const listos = new WeakSet();

  /** Devuelve el scroller horizontal de una tarjeta, si lo tiene. */
  const scroller = (galeria) => galeria.querySelector('slideshow-slides');

  /**
   * Fija la tarjeta en su primera imagen. `scrollLeft = 0` respeta el sentido
   * de lectura: en RTL el origen sigue siendo la primera diapositiva.
   */
  const fijar = (galeria) => {
    const sc = scroller(galeria);
    if (!sc || galeria.dataset.siggoTocada === '1') return;
    if (Math.abs(sc.scrollLeft) < 0.5) return;

    // `scroll-behavior: smooth` esta activo en el sitio: sin desactivarlo aqui,
    // la correccion se veria como un barrido lateral en cada carga.
    const previo = sc.style.scrollBehavior;
    sc.style.scrollBehavior = 'auto';
    sc.scrollLeft = 0;
    sc.style.scrollBehavior = previo;
  };

  /**
   * A partir de la primera interaccion real, la tarjeta se gobierna sola: el
   * hover, las flechas y el selector de variante mandan sobre la posicion.
   */
  const soltar = (galeria) => {
    galeria.dataset.siggoTocada = '1';
  };

  const preparar = (galeria) => {
    if (listos.has(galeria)) return;
    const sc = scroller(galeria);
    if (!sc) return;
    listos.add(galeria);

    fijar(galeria);

    for (const evento of ['pointerenter', 'pointerdown', 'touchstart', 'focusin', 'keydown']) {
      galeria.addEventListener(evento, () => soltar(galeria), { once: true, passive: true });
    }

    // El ancla obligatoria se reevalua cuando cambia el tamaño del scroller o
    // el de sus diapositivas; es ahi donde puede engancharse a la segunda.
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => {
        if (galeria.dataset.siggoTocada === '1') return ro.disconnect();
        fijar(galeria);
      });
      ro.observe(sc);
    }

    // Una imagen que llega tarde cambia el tamaño de su diapositiva.
    galeria.querySelectorAll('img').forEach((img) => {
      if (img.complete) return;
      img.addEventListener('load', () => fijar(galeria), { once: true });
    });
  };

  const barrer = (raiz = document) => {
    raiz.querySelectorAll?.(SELECTOR).forEach(preparar);
  };

  const arrancar = () => {
    barrer();

    // Las secciones diferidas y las respuestas de la Section Rendering API
    // insertan tarjetas despues de la carga inicial.
    new MutationObserver((mutaciones) => {
      for (const m of mutaciones) {
        for (const nodo of m.addedNodes) {
          if (nodo.nodeType !== 1) continue;
          if (nodo.matches?.(SELECTOR)) preparar(nodo);
          else barrer(nodo);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar, { once: true });
  } else {
    arrancar();
  }

  // Al volver atras el navegador restaura la posicion de los scrollers
  // internos, incluido el de cada tarjeta. Es el caso mas facil de reproducir.
  window.addEventListener('pageshow', () => {
    document.querySelectorAll(SELECTOR).forEach((g) => {
      delete g.dataset.siggoTocada;
      fijar(g);
    });
  });
})();
